import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Users, Flag,
  Ban, CircleCheck, ShieldCheck, Search, TrendingUp, Play,
} from "lucide-react";
import { api, getToken } from "../api";
import { useAuth } from "../auth";
import { fmtNum, timeAgo, localApi } from "../config";
import { Spinner, Empty, Avatar, SectionTitle } from "../components/cards";

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.02] p-5 ${className}`}>{children}</div>
);

const Pill = ({ tone = "slate", children, testId }) => {
  const tones = {
    slate: "bg-white/10 text-slate-300",
    green: "bg-emerald-500/15 text-emerald-400",
    red: "bg-crimson/15 text-crimson",
    amber: "bg-amber-500/15 text-amber-400",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-wider ${tones[tone]}`} data-testid={testId}>
      {children}
    </span>
  );
};

const statusTone = (s) => (s === "approved" || s === true ? "green" : s === "pending" ? "amber" : s === "rejected" || s === "suspended" ? "red" : "slate");

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "users", label: "Users", icon: Users },
  { key: "reports", label: "Reports", icon: Flag },
  { key: "earnings", label: "Earnings", icon: TrendingUp },
];

const StatCard = ({ label, value, testId, accent }) => (
  <Card className="text-center">
    <p className={`font-heading text-3xl font-black ${accent || "text-white"}`} data-testid={testId}>{value}</p>
    <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
  </Card>
);

const Overview = () => {
  const [ov, setOv] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then(setOv).catch(() => setOv({}));
  }, []);
  if (ov === null) return <Spinner />;
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6" data-testid="admin-overview">
      <StatCard label="Users" value={fmtNum(ov.users)} testId="stat-users" />
      <StatCard label="Videos" value={fmtNum(ov.videos)} testId="stat-videos" />
      <StatCard label="Tracks" value={fmtNum(ov.tracks)} testId="stat-tracks" />
      <StatCard label="Pending reports" value={fmtNum(ov.pendingReports)} testId="stat-pending-reports" accent="text-amber-400" />
      <StatCard label="Suspended" value={fmtNum(ov.suspended)} testId="stat-suspended" accent="text-crimson" />
    </div>
  );
};

const UsersTab = () => {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(Array.isArray(r) ? r : [])).catch(() => setUsers([]));
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!users) return [];
    const t = q.trim().toLowerCase();
    if (!t) return users;
    return users.filter(
      (u) =>
        (u.displayName || "").toLowerCase().includes(t) ||
        (u.username || "").toLowerCase().includes(t) ||
        (u.email || "").toLowerCase().includes(t)
    );
  }, [users, q]);

  const toggleSuspend = async (u) => {
    setBusy(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, suspended: r.suspended ?? !u.suspended } : x)));
    } catch {}
    setBusy(null);
  };

  if (users === null) return <Spinner />;

  return (
    <div data-testid="admin-users">
      <div className="mb-5 flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5">
        <Search size={16} className="shrink-0 text-slate-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search users by name, username or email"
          data-testid="admin-user-search"
          className="w-full bg-transparent text-sm text-white placeholder-slate-600 outline-none"
        />
      </div>
      {filtered.length === 0 ? (
        <Empty>No users found.</Empty>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/10">
          <div className="hidden grid-cols-[1.6fr_1.2fr_0.8fr_1fr] gap-3 border-b border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 md:grid">
            <span>User</span><span>Email</span><span>Status</span><span className="text-right">Action</span>
          </div>
          {filtered.map((u) => (
            <div key={u.id} className="flex flex-col gap-3 border-b border-white/5 px-4 py-3 last:border-0 md:grid md:grid-cols-[1.6fr_1.2fr_0.8fr_1fr] md:items-center md:gap-3" data-testid={`admin-user-${u.id}`}>
              <div className="flex min-w-0 items-center gap-3">
                <Avatar user={u} size={34} />
                <div className="min-w-0">
                  <p className="truncate font-heading text-sm font-semibold text-white">{u.displayName || u.username}</p>
                  <p className="truncate text-xs text-slate-500">@{u.username}</p>
                </div>
                {u.isAdmin && <ShieldCheck size={14} className="shrink-0 text-crimson" title="Admin" />}
              </div>
              <p className="truncate text-xs text-slate-400">{u.email}</p>
              <div><Pill tone={u.suspended ? "red" : "green"} testId={`user-status-${u.id}`}>{u.suspended ? "Suspended" : "Active"}</Pill></div>
              <div className="md:text-right">
                {!u.isAdmin && (
                  <button
                    onClick={() => toggleSuspend(u)}
                    disabled={busy === u.id}
                    data-testid={`user-suspend-${u.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-heading text-[11px] font-bold transition-colors ${
                      u.suspended
                        ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                        : "border-crimson/40 text-crimson hover:bg-crimson/10"
                    } disabled:opacity-50`}
                  >
                    {u.suspended ? <CircleCheck size={13} /> : <Ban size={13} />}
                    {u.suspended ? "Unsuspend" : "Suspend"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReportsTab = () => {
  const [reports, setReports] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/admin/reports").then((r) => setReports(Array.isArray(r) ? r : [])).catch(() => setReports([]));
  }, []);

  const resolve = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/admin/reports/${r.id}/resolve`);
      setReports((list) => list.map((x) => (x.id === r.id ? { ...x, status: "resolved" } : x)));
    } catch {}
    setBusy(null);
  };

  if (reports === null) return <Spinner />;
  const pending = reports.filter((r) => r.status === "pending");
  const done = reports.filter((r) => r.status !== "pending");

  const Row = ({ r }) => (
    <Card className="flex flex-col gap-3 md:flex-row md:items-center" data-testid={`admin-report-${r.id}`}>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Avatar user={r.reporter} size={36} />
        <div className="min-w-0">
          <p className="truncate text-sm text-white"><span className="font-semibold">{r.reporter?.displayName || r.reporter?.username}</span> reported a {r.targetType}</p>
          <p className="truncate text-xs text-slate-500">{r.reason}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">{timeAgo(r.createdAt)}</span>
        <Pill tone={r.status === "pending" ? "amber" : "green"} testId={`report-status-${r.id}`}>{r.status}</Pill>
        {r.status === "pending" && (
          <button onClick={() => resolve(r)} disabled={busy === r.id} data-testid={`report-resolve-${r.id}`} className="rounded-full border border-emerald-500/40 px-3.5 py-1.5 font-heading text-[11px] font-bold text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-50">
            Resolve
          </button>
        )}
      </div>
    </Card>
  );

  return (
    <div data-testid="admin-reports">
      <SectionTitle>Pending ({pending.length})</SectionTitle>
      {pending.length === 0 ? <Empty>No pending reports.</Empty> : <div className="space-y-3">{pending.map((r) => <Row key={r.id} r={r} />)}</div>}
      {done.length > 0 && (
        <div className="mt-10">
          <SectionTitle>Resolved</SectionTitle>
          <div className="space-y-3">{done.map((r) => <Row key={r.id} r={r} />)}</div>
        </div>
      )}
    </div>
  );
};

// Earnings: paste the app's revenue for the period and run it across creators.
const EarningsTab = () => {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [amount, setAmount] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [note, setNote] = useState(null); // {tone,text}

  const load = () => {
    localApi("GET", "/admin/earnings/preview", undefined, getToken()).then(setPreview).catch(() => setPreview(null));
    localApi("GET", "/admin/earnings/history", undefined, getToken()).then((r) => setHistory(Array.isArray(r) ? r : [])).catch(() => setHistory([]));
  };
  useEffect(() => {
    load();
  }, []);

  const run = async () => {
    setRunning(true);
    setNote(null);
    try {
      const r = await localApi("POST", "/admin/earnings/run", { amount: parseFloat(amount) }, getToken());
      setNote({ tone: "ok", text: `Revenue run complete — £${(r.amount ?? 0).toFixed(2)} paid to ${r.creators_paid} creator${r.creators_paid === 1 ? "" : "s"}.` });
      setAmount("");
      setConfirming(false);
      load();
    } catch (err) {
      setNote({ tone: "err", text: err.message || "Run failed." });
    }
    setRunning(false);
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div data-testid="admin-earnings" className="max-w-2xl space-y-6">
      <Card>
        <SectionTitle>Run revenue</SectionTitle>
        <p className="mb-4 text-sm leading-relaxed text-slate-400">
          Paste the revenue the app generated since the last run, then run it. The total is shared between videos in proportion to each video's estimated earnings, and each creator's share goes into their wallet.
        </p>
        {preview && (
          <div className="mb-5 grid grid-cols-3 gap-3" data-testid="earnings-preview">
            <div className="rounded-xl bg-white/[0.04] p-3 text-center">
              <p className="font-heading text-lg font-black text-white">£{(preview.total_estimated ?? 0).toFixed(2)}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Total estimates</p>
            </div>
            <div className="rounded-xl bg-white/[0.04] p-3 text-center">
              <p className="font-heading text-lg font-black text-white">{preview.creators_to_be_paid}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Creators to pay</p>
            </div>
            <div className="rounded-xl bg-white/[0.04] p-3 text-center">
              <p className="font-heading text-lg font-black text-white">{preview.videos_with_estimates}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Videos</p>
            </div>
          </div>
        )}
        {preview && (
          <p className="mb-4 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            Period: {preview.period_start ? fmtDate(preview.period_start) : "All time"} → {fmtDate(preview.period_end)}
          </p>
        )}
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">£</span>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Revenue amount" data-testid="earnings-amount" className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-3 pl-8 pr-4 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60" />
          </div>
          <button onClick={() => setConfirming(true)} disabled={!amount || parseFloat(amount) <= 0} data-testid="earnings-run" className="inline-flex items-center justify-center gap-2 rounded-full bg-crimson px-6 py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
            <Play size={15} /> Run
          </button>
        </div>
        {note && <p className={`mt-4 rounded-xl border px-4 py-3 text-sm ${note.tone === "ok" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-crimson/30 bg-crimson/10 text-crimson"}`} data-testid="earnings-note">{note.text}</p>}
      </Card>

      <div>
        <SectionTitle>Run history</SectionTitle>
        {history.length === 0 ? (
          <Card><p className="text-sm text-slate-500" data-testid="earnings-no-history">No revenue runs yet.</p></Card>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-white/10" data-testid="earnings-history">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between gap-3 border-b border-white/5 px-4 py-3 last:border-0" data-testid={`earnings-run-${i}`}>
                <div>
                  <p className="font-heading text-sm font-semibold text-white">£{(h.amount ?? 0).toFixed(2)}</p>
                  <p className="text-xs text-slate-500">{fmtDate(h.date)}</p>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{h.creators_paid} creator{h.creators_paid === 1 ? "" : "s"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirming && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !running && setConfirming(false)} data-testid="earnings-confirm">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0c0c12] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold text-white">Confirm revenue run</h3>
            <div className="mt-4 space-y-2 text-sm text-slate-300">
              <p>Amount: <span className="font-bold text-white">£{parseFloat(amount || 0).toFixed(2)}</span></p>
              <p>Period: <span className="text-slate-400">{preview?.period_start ? fmtDate(preview.period_start) : "All time"} → {preview ? fmtDate(preview.period_end) : ""}</span></p>
              <p>Total estimates: <span className="text-slate-400">£{(preview?.total_estimated ?? 0).toFixed(2)}</span></p>
              <p>Creators to be paid: <span className="text-slate-400">{preview?.creators_to_be_paid}</span></p>
            </div>
            <p className="mt-4 text-xs leading-relaxed text-slate-500">This credits each creator's wallet. It can't be undone.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setConfirming(false)} disabled={running} className="rounded-full border border-white/15 px-5 py-2.5 font-heading text-xs font-bold text-slate-300 hover:border-white/40" data-testid="earnings-confirm-cancel">Cancel</button>
              <button onClick={run} disabled={running} className="rounded-full bg-crimson px-5 py-2.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover disabled:opacity-50" data-testid="earnings-confirm-run">{running ? "Running…" : "Confirm & run"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminPanel = () => {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("overview");

  if (!ready) return <Spinner />;
  if (!user || !(user.isAdmin || user.isSuperAdmin)) {
    return (
      <div className="mx-auto max-w-md py-24 text-center" data-testid="admin-forbidden">
        <ShieldCheck size={40} className="mx-auto text-slate-700" />
        <h1 className="mt-4 font-heading text-xl font-bold text-white">Admins only</h1>
        <p className="mt-2 text-sm text-slate-500">You need an administrator account to open the control panel.</p>
        <Link to="/app" className="mt-6 inline-block rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover" data-testid="admin-forbidden-home">
          Back to Home
        </Link>
      </div>
    );
  }

  const Active = { overview: Overview, users: UsersTab, reports: ReportsTab, earnings: EarningsTab }[tab];

  return (
    <div className="mx-auto max-w-6xl" data-testid="admin-panel">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-crimson/15 text-crimson"><ShieldCheck size={20} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-white">Admin control panel</h1>
          <p className="text-sm text-slate-500">Manage Michael C Streamline — same controls as the mobile app.</p>
        </div>
      </div>

      <div className="mb-8 flex gap-1 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02] p-1" data-testid="admin-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`admin-tab-${key}`}
            className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 font-heading text-xs font-bold transition-colors ${
              tab === key ? "bg-crimson text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <Active />
    </div>
  );
};

export default AdminPanel;
